/**
 * #409 (G51): the point-in-time ("as of") adapter — the one place the web app knows how the
 * API spells a historical read. Everything else speaks a calendar day (`YYYY-MM-DD`, UTC) or
 * `null` for "now".
 *
 * - URL state: `?as_of=YYYY-MM-DD` on the Registry and the Readiness Map; absent means now.
 * - API: the same `as_of` query parameter on GET /api/entities, /api/entities/{id}, the expand
 *   route and the readiness reads. A day means "at the end of that day" (UTC).
 * - Responses carry `as_of` and `is_historical: true` when not now; a write under a historical
 *   `as_of` is refused 422 `as_of_read_only` — the backstop, never the UX (rows read historically
 *   come back with editing switched off, below).
 */
import type { EntityRow, RowPermissions } from '@/api/types';
import { formatDate } from './format';

export const AS_OF_PARAM = 'as_of';
export const AS_OF_READ_ONLY = 'as_of_read_only';
const DAY_MS = 86_400_000;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Today as a UTC calendar day. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** A real calendar day in `YYYY-MM-DD` form (rejects 2026-02-30 and free text). */
export function isDay(value: string | null | undefined): value is string {
  if (!value || !DAY.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/**
 * The view's as-of day from the URL, or null for now. A malformed value, today or a future day
 * all read as now — there is nothing historical to show about them.
 */
export function readAsOf(params: URLSearchParams, today: string = todayUtc()): string | null {
  const raw = params.get(AS_OF_PARAM);
  if (!isDay(raw)) return null;
  return raw < today ? raw : null;
}

/** `params` with the as-of day set, or removed for now (today or later also means now). */
export function withAsOf(params: URLSearchParams, day: string | null, today: string = todayUtc()): URLSearchParams {
  const next = new URLSearchParams(params);
  if (day && isDay(day) && day < today) next.set(AS_OF_PARAM, day);
  else next.delete(AS_OF_PARAM);
  return next;
}

export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
}

/** The slider's model: one step per day from the data's earliest day to today (the last step is "Now"). */
export interface AsOfRange {
  earliest: string;
  today: string;
  /** steps 0..max; max is today */
  max: number;
}

/** Without an earliest day from the server, the slider reaches back this far. */
export const FALLBACK_SPAN_DAYS = 365;

export function asOfRange(earliest: string | null | undefined, today: string = todayUtc()): AsOfRange {
  const start = isDay(earliest) && earliest <= today ? earliest : addDays(today, -FALLBACK_SPAN_DAYS);
  return { earliest: start, today, max: daysBetween(start, today) };
}

export function dayToStep(range: AsOfRange, day: string | null): number {
  if (!day) return range.max;
  return Math.min(range.max, Math.max(0, daysBetween(range.earliest, day)));
}

/** The day for a slider step; the last step is now (null). */
export function stepToDay(range: AsOfRange, step: number): string | null {
  const s = Math.min(range.max, Math.max(0, Math.round(step)));
  return s >= range.max ? null : addDays(range.earliest, s);
}

/** The value the API receives for a day: the end of that day, UTC. */
export function apiAsOf(day: string): string {
  return `${day}T23:59:59.999Z`;
}

/** Append the API's as-of parameter to a query string builder when the view is historical. */
export function appendAsOf(params: URLSearchParams, day: string | null | undefined): URLSearchParams {
  if (day) params.set(AS_OF_PARAM, apiAsOf(day));
  return params;
}

/** A path with the as-of parameter added (or unchanged for now). */
export function pathWithAsOf(path: string, day: string | null | undefined): string {
  if (!day) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${AS_OF_PARAM}=${encodeURIComponent(apiAsOf(day))}`;
}

/** What a response says about its own point in time (`as_of`, `is_historical`, the data's earliest day). */
export interface AsOfMeta {
  asOf: string | null;
  isHistorical: boolean;
  earliest: string | null;
}

export function readAsOfMeta(raw: unknown): AsOfMeta {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const day = (v: unknown) => (typeof v === 'string' && isDay(v.slice(0, 10)) ? v.slice(0, 10) : null);
  const range = (r.as_of_range ?? r.asOfRange) as Record<string, unknown> | undefined;
  return {
    asOf: day(r.as_of ?? r.asOf),
    isHistorical: r.is_historical === true || r.isHistorical === true,
    earliest: day(range?.earliest ?? r.earliest_valid_from ?? r.earliestValidFrom),
  };
}

export function readOnlyReason(day: string): string {
  return `Viewing as of ${formatAsOf(day)} — read-only`;
}

/** Rows read as of a past day cannot be edited: every editor already honours `permissions.edit`. */
export function freezePermissions(day: string | null | undefined, perms: RowPermissions): RowPermissions {
  return day ? { edit: false, reason: readOnlyReason(day) } : perms;
}

export function freezeRows<T extends Pick<EntityRow, 'permissions'>>(day: string | null | undefined, rows: T[]): T[] {
  return day ? rows.map((row) => ({ ...row, permissions: freezePermissions(day, row.permissions) })) : rows;
}

/** The as-of day as every date reads (R16 T-137): 2026-10-09. */
export function formatAsOf(day: string): string {
  return formatDate(day);
}
